import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LogisticsStatusBadge,
  LogisticsTypeBadge,
} from '@/modules/logistics/components';
import { useLogisticsOrders, useLogisticsProviders } from '@/modules/logistics/hooks';
import {
  LOGISTICS_ORDER_STATUS_LABELS,
  LOGISTICS_ORDER_TYPE_LABELS,
  type LogisticsOrderStatus,
  type LogisticsOrderType,
} from '@/modules/logistics/types';
import {
  filterLogisticsOrders,
  type LogisticsOrderListFilters,
} from '@/modules/logistics/utils/orderFilters';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

const STATUS_OPTIONS: Array<LogisticsOrderStatus | 'all'> = [
  'all',
  'pending',
  'scheduled',
  'in_transit',
  'delivered',
  'returned',
  'cancelled',
];

const TYPE_OPTIONS: Array<LogisticsOrderType | 'all'> = [
  'all',
  'delivery',
  'pickup',
  'transfer',
];

export function LogisticsOrdersListPage() {
  const { orders, loading, error } = useLogisticsOrders();
  const { providers } = useLogisticsProviders();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('logistics.write');

  const [statusFilter, setStatusFilter] = useState<LogisticsOrderStatus | 'all'>(
    'all',
  );
  const [typeFilter, setTypeFilter] = useState<LogisticsOrderType | 'all'>('all');
  const [providerId, setProviderId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () =>
      filterLogisticsOrders(orders, {
        status: statusFilter,
        type: typeFilter,
        ...(providerId ? { providerId } : {}),
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
        search,
      } satisfies LogisticsOrderListFilters),
    [orders, statusFilter, typeFilter, providerId, dateFrom, dateTo, search],
  );

  const providerName = (id: string | null) =>
    id ? (providers.find((p) => p.id === id)?.name ?? '—') : '—';

  return (
    <section>
      <div className="inventory-page-header">
        <h1>Órdenes logísticas</h1>
        {canWrite ? (
          <Link
            to={`${modulePaths.logistics}/nueva`}
            className="auth-button inventory-page-header__btn"
          >
            Nueva orden
          </Link>
        ) : null}
      </div>

      <div className="reservation-filters users-form-grid">
        <label className="auth-label">
          Estado
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as LogisticsOrderStatus | 'all')
            }
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'Todos' : LOGISTICS_ORDER_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="auth-label">
          Tipo
          <select
            value={typeFilter}
            onChange={(e) =>
              setTypeFilter(e.target.value as LogisticsOrderType | 'all')
            }
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t === 'all' ? 'Todos' : LOGISTICS_ORDER_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="auth-label">
          Proveedor
          <select
            value={providerId}
            onChange={(e) => setProviderId(e.target.value)}
          >
            <option value="">Todos</option>
            {providers
              .filter((p) => p.active)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </label>
        <label className="auth-label">
          Desde
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </label>
        <label className="auth-label">
          Hasta
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </label>
        <label className="auth-label">
          Buscar
          <input
            type="search"
            placeholder="Dirección, contacto, notas"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </div>

      {loading ? <p className="auth-muted">Cargando órdenes…</p> : null}
      {error ? <p className="auth-error">{error}</p> : null}

      {!loading && filtered.length === 0 ? (
        <p className="auth-muted">No hay órdenes con los filtros aplicados.</p>
      ) : null}

      {!loading && filtered.length > 0 ? (
        <table className="users-table">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Fecha</th>
              <th>Estado</th>
              <th>Proveedor</th>
              <th>Reserva</th>
              <th>Ítems</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((o) => (
              <tr key={o.id}>
                <td>
                  <LogisticsTypeBadge type={o.type} />
                </td>
                <td>{o.scheduledDate}</td>
                <td>
                  <LogisticsStatusBadge status={o.status} />
                </td>
                <td>{providerName(o.providerId)}</td>
                <td>
                  {o.reservationId ? (
                    <Link
                      to={`${modulePaths.reservations}/${o.reservationId}`}
                      className="link-button"
                    >
                      Ver reserva
                    </Link>
                  ) : (
                    '—'
                  )}
                </td>
                <td>{o.inventoryItemIds.length}</td>
                <td>
                  <Link
                    to={`${modulePaths.logistics}/${o.id}`}
                    className="link-button"
                  >
                    {canWrite ? 'Editar' : 'Ver'}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}
